import api, { handleResponse } from "../queries/api";
import { QueryFunctionContext } from "@tanstack/react-query";
import { ICompanyModel, IDeviceModel, IGasMapping, UserModel } from "../pages/user/CompanyPage";
import {
  ChangeDeviceModel,
  ConnectDeviceModel,
  IPreference,
  IWeatherData,
  IWeatherDataRange,
  RegisterDeviceDto,
  ReportRequestDto,
  ReportItem,
  SendEmailDto,
  ParameterCreateDto,
  ParameterUpdateDto,
} from "../types/device";
import { ClientDetailModel, ClientModel, CustomResponse } from "../types/user";
import { ParameterFormData } from "../components/widgets/table/AddParameterModal";
import { Dashboard } from "../pages/dashboard/types/dashboard";
import { Alert, AlertCreateDto, AlertUpdateDto } from "../types/alert";
import { AlertHistory } from "../pages/alerts/components/AlertFeed";
import {
  DownloadRequestDto,
  DownloadResponseDto,
  UploadRequestDto,
  UploadResponseDto,
} from "../types/aws";
import { Device } from "../pages/new/DeviceSelection";
import { Location } from "../pages/new/LocationSelection";

// Base path generators
const getClientBasePath = () => "/client";
const getDeviceBasePath = () => "/device";
const getLocationBasePath = () => "/location";
const getUserBasePath = () => "/user";
const getWeatherBasePath = () => "/weather";
const getEmailBasePath = () => "/email";
const getAwsBasePath = () => "/aws";
const getBillingBasePath = () => "/billing";

// ==========================================
// CLIENT CRUD OPERATIONS
// ==========================================

// CREATE
export async function addClient(clientDetail: ClientModel): Promise<CustomResponse<any>> {
  return api.post(`${getClientBasePath()}/create`, clientDetail).then(handleResponse);
}

// READ
export async function getAllClients(): Promise<ClientDetailModel[]> {
  return api.get(`${getClientBasePath()}/all/details`).then((res) => res.data.data);
}

export async function getClientDetail(context: QueryFunctionContext): Promise<ICompanyModel> {
  const id = context.queryKey[2];
  return api.get(`${getClientBasePath()}/${id}`).then((res) => res.data.data);
}

// UPDATE
export async function editClient(clientDetail: ClientModel): Promise<CustomResponse<any>> {
  return api.post(`${getClientBasePath()}/update`, clientDetail).then(handleResponse);
}

// DELETE
export async function deleteClient(clientId: string): Promise<CustomResponse<any>> {
  return api.delete(`${getClientBasePath()}/${clientId}`).then(handleResponse);
}

// ==========================================
// DEVICE CRUD OPERATIONS
// ==========================================

// CREATE
export async function createDevice(deviceData: {
  name: string;
  dataRate: number;
  image?: string;
  locationId: string;
}): Promise<CustomResponse<any>> {
  return api.post(`${getDeviceBasePath()}`, deviceData).then(handleResponse);
}

// READ
export async function getAllDevices(): Promise<IDeviceModel[]> {
  return api.get(`${getDeviceBasePath()}/all`).then((res) => res.data.data);
}

export async function getUserDevices(): Promise<UserModel> {
  return api.get(`${getDeviceBasePath()}/user`).then((res) => res.data.data);
}

export async function getBillingData(): Promise<any> {
  return api.get(`${getBillingBasePath()}`).then((res) => res.data.data);
}

export async function getDevicesRange(context: QueryFunctionContext): Promise<IWeatherDataRange> {
  const id = context.queryKey[1];
  return api.get(`${getDeviceBasePath()}/range?id=${id}`).then((res) => res.data.data);
}

export async function getDevicesForLocation(context: QueryFunctionContext): Promise<Device[]> {
  // queryKey structure: ["devices", "location", locationId]
  const locationId = context.queryKey[1] as string;
  return api.get(`${getDeviceBasePath()}/all/${locationId}`).then((res) => res.data.data);
}

// UPDATE
export async function updateDevice(
  deviceId: string,
  deviceData: Partial<IDeviceModel>
): Promise<CustomResponse<any>> {
  return api.put(`${getDeviceBasePath()}/${deviceId}`, deviceData).then(handleResponse);
}

export async function updateDeviceRange(
  weatherData: IWeatherDataRange
): Promise<CustomResponse<string>> {
  return api.put(`${getDeviceBasePath()}/range`, weatherData).then(handleResponse);
}

// DELETE
export async function deleteDevice(deviceId: string): Promise<CustomResponse<any>> {
  return api.delete(`${getDeviceBasePath()}/${deviceId}`).then(handleResponse);
}

// Device-specific operations
export async function registerDevice(
  registerDeviceDto: RegisterDeviceDto
): Promise<CustomResponse<any>> {
  return api.post(`${getDeviceBasePath()}/register`, registerDeviceDto).then(handleResponse);
}

export async function connectDeviceWithClient(
  connectDevice: ConnectDeviceModel
): Promise<CustomResponse<any>> {
  return api
    .post(`${getDeviceBasePath()}/connectDeviceWithClient`, connectDevice)
    .then(handleResponse);
}

export async function changeDeviceState(
  deviceModel: ChangeDeviceModel
): Promise<CustomResponse<any>> {
  return api.put(`${getDeviceBasePath()}/updateStatus`, deviceModel).then(handleResponse);
}

// ==========================================
// USER MANAGEMENT OPERATIONS
// ==========================================

// READ
export async function getAllUsers(): Promise<any[]> {
  return api.get(`${getUserBasePath()}/all`).then((res) => res.data.data);
}

// CREATE
export async function signUpUser(data: {
  email: string;
  name: string;
  password?: string;
  role?: "ADMIN" | "USER";
}): Promise<CustomResponse<any>> {
  return api.post(`${getUserBasePath()}/signUp`, data).then(handleResponse);
}

// Check whether an email is already registered on the platform (admin-only)
export async function checkUserExists(
  email: string,
): Promise<CustomResponse<{ exists: boolean; name: string | null; alreadyMember: boolean }>> {
  return api
    .get(`${getUserBasePath()}/exists`, { params: { email } })
    .then(handleResponse);
}

// DELETE
export async function deleteUser(id: string): Promise<CustomResponse<any>> {
  return api.delete(`${getUserBasePath()}/${id}`).then(handleResponse);
}

// DEACTIVATE
export async function deactivateUser(
  email: string,
  deactivated: boolean,
): Promise<CustomResponse<any>> {
  return api
    .get(`${getUserBasePath()}/deactive`, { params: { email, deactivated } })
    .then(handleResponse);
}

// User preferences
export async function getUserPreference(context: QueryFunctionContext): Promise<IPreference> {
  const id = context.queryKey[1];
  return api.get(`${getUserBasePath()}/preference?id=${id}`).then((res) => res.data.data);
}

export async function updatePreference(
  preferenceData: IPreference
): Promise<CustomResponse<string>> {
  return api.put(`${getUserBasePath()}/preference`, preferenceData).then(handleResponse);
}

export async function generateCredentials(email: string): Promise<CustomResponse<any>> {
  return api.post(`${getUserBasePath()}/generateCredentials`, { email }).then(handleResponse);
}

// UPDATE ROLE
export async function updateUserRole(
  id: string,
  role: string,
): Promise<CustomResponse<any>> {
  return api.put(`${getUserBasePath()}/${id}/role`, { role }).then(handleResponse);
}

// UNASSIGNED USERS
export async function getUnassignedUsers(): Promise<any[]> {
  return api.get(`${getUserBasePath()}/unassigned`).then((res) => res.data.data);
}

// ASSIGN USER TO CLIENT
export async function assignUserToClient(
  userId: string,
  clientId: string,
): Promise<CustomResponse<any>> {
  return api.put(`${getUserBasePath()}/${userId}/assign-client`, { clientId }).then(handleResponse);
}

// SWITCH USER CLIENT
export async function switchUserClient(clientId: string): Promise<CustomResponse<any>> {
  return api.put(`${getUserBasePath()}/switch-client`, { clientId }).then(handleResponse);
}

// UPDATE PROFILE
export async function updateProfile(data: {
  name?: string;
  phone?: string;
  address?: string;
  gstNo?: string;
  profileImage?: string;
  organisationName?: string;
}): Promise<CustomResponse<any>> {
  return api.put(`${getUserBasePath()}/profile`, data).then(handleResponse);
}

// AVAILABLE CLIENTS (public, for signup)
export async function getAvailableClients(): Promise<{ id: string; name: string; logo: string }[]> {
  return api.get(`${getUserBasePath()}/available-clients`).then((res) => res.data.data);
}

// ONBOARD ORGANIZATION (create or join during onboarding)
export async function onboardOrganization(data: {
  organizationName?: string;
  clientId?: string;
}): Promise<CustomResponse<{ id: string; token: string; clientId: string; role: string }>> {
  return api.post(`${getUserBasePath()}/onboard-org`, data).then(handleResponse);
}

// ==========================================
// CLIENT-SPECIFIC OPERATIONS
// ==========================================

export async function editGasMapping(gasMapping: IGasMapping): Promise<CustomResponse<string>> {
  return api.put(`${getClientBasePath()}/mapping`, gasMapping).then(handleResponse);
}

// ==========================================
// WEATHER DATA OPERATIONS
// ==========================================

export async function getWeatherData(context: QueryFunctionContext): Promise<IWeatherData[]> {
  const deviceId = context.queryKey[2] as string;
  const metric = context.queryKey[3] as string;
  const days = context.queryKey[4] as number;
  const ms = context.queryKey[5] as number;
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - days);

  return api
    .get(
      `${getWeatherBasePath()}/data/${deviceId}/${startDate.toISOString()}/${endDate.toISOString()}/${metric}/${ms}`
    )
    .then((res) => res.data.data);
}

export async function getLiveWeatherData(context: QueryFunctionContext): Promise<IWeatherData> {
  const deviceId = context.queryKey[2] as string;
  return api.get(`${getWeatherBasePath()}/latest/${deviceId}`).then((res) => res.data.data);
}

export async function generateReport(reportReqDto: ReportRequestDto): Promise<string> {
  return api.post(`${getWeatherBasePath()}/report`, reportReqDto).then((res) => res.data.data);
}

export async function getReports(): Promise<ReportItem[]> {
  return api.get(`${getWeatherBasePath()}/reports`).then((res) => res.data.data);
}

export async function downloadReport(jobId: string, format: "xlsx" | "pdf" = "xlsx"): Promise<void> {
  const response = await api.get(`${getWeatherBasePath()}/report/${jobId}/download`, {
    params: { format },
    responseType: "blob",
  });
  const mimeType = format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const blob = new Blob([response.data], { type: mimeType });
  const contentDisposition = response.headers["content-disposition"];
  let filename = `report.${format}`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) filename = match[1];
  }
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function deleteReport(jobId: string): Promise<string> {
  return api.delete(`${getWeatherBasePath()}/report/${jobId}`).then((res) => res.data.data);
}

// ==========================================
// LOCATION CRUD OPERATIONS
// ==========================================

// CREATE
export async function createLocation(locationData: {
  name: string;
  address?: string;
  image?: string;
}): Promise<CustomResponse<any>> {
  return api.post(`${getLocationBasePath()}/create`, locationData).then(handleResponse);
}

// READ
export async function getAllLocations(): Promise<any[]> {
  return api.get(`${getLocationBasePath()}/all/details`).then((res) => res.data.data);
}

export async function getLocation(context: QueryFunctionContext): Promise<Location> {
  const id = context.queryKey[2];
  return api.get(`${getLocationBasePath()}/${id}`).then((res) => res.data.data);
}

// UPDATE
export async function updateLocation(locationData: {
  id: string;
  name: string;
  address?: string;
  imageKey?: string;
  planKey?: string;
}): Promise<CustomResponse<any>> {
  const { id, ...updateData } = locationData;
  return api.put(`${getLocationBasePath()}/${id}`, updateData).then(handleResponse);
}

// DELETE
export async function deleteLocation(locationId: string): Promise<CustomResponse<any>> {
  return api.delete(`${getLocationBasePath()}/${locationId}`).then(handleResponse);
}

// ==========================================
// PARAMETER CRUD OPERATIONS
// ==========================================

// CREATE
export async function createParameter(
  parameterData: ParameterCreateDto
): Promise<CustomResponse<any>> {
  try {
    const response = await api.post(`${getDeviceBasePath()}/parameter`, parameterData);
    return handleResponse(response);
  } catch (error: any) {
    // Handle specific backend errors according to specification
    if (
      error.response?.status === 500 &&
      error.response?.data?.description?.includes("Duplicate identifier")
    ) {
      throw new Error("Parameter identifier already exists for this device");
    }
    throw error;
  }
}

// READ
export async function getDeviceParameters(
  context: QueryFunctionContext
): Promise<ParameterFormData[]> {
  const deviceId = context.queryKey[2];
  return api.get(`${getDeviceBasePath()}/parameter/${deviceId}`).then((res) => res.data.data);
}

// UPDATE
export async function updateParameter(
  parameterId: string,
  parameterData: ParameterUpdateDto
): Promise<CustomResponse<any>> {
  try {
    const response = await api.put(
      `${getDeviceBasePath()}/parameter/${parameterId}`,
      parameterData
    );
    return handleResponse(response);
  } catch (error: any) {
    // Handle specific backend errors according to specification
    if (
      error.response?.status === 500 &&
      error.response?.data?.description?.includes("Duplicate identifier")
    ) {
      throw new Error("Parameter identifier already exists for this device");
    }
    throw error;
  }
}

// DELETE
export async function deleteParameter(parameterId: string): Promise<CustomResponse<any>> {
  try {
    const response = await api.delete(`${getDeviceBasePath()}/parameter/${parameterId}`);
    return handleResponse(response);
  } catch (error: any) {
    // Handle parameter not found or other specific errors
    if (error.response?.status === 404) {
      throw new Error("Parameter not found");
    }
    throw error;
  }
}

// ==========================================
// EMAIL OPERATIONS
// ==========================================

export async function sendEmail(sendEmailDto: SendEmailDto): Promise<CustomResponse<any>> {
  return api.post(`${getEmailBasePath()}/sendEmail`, sendEmailDto).then(handleResponse);
}

export async function getDashboardData(deviceId: string): Promise<Dashboard> {
  return api.get(`${getDeviceBasePath()}/dashboard/${deviceId}`).then((res) => res.data.data);
}

export async function saveDashboardData(
  data: { dashboardData: Dashboard },
  deviceId: string
): Promise<void> {
  return api
    .post(`${getDeviceBasePath()}/dashboard/${deviceId}`, data)
    .then((res) => res.data.data);
}
export async function resetDashboardData(deviceId: string): Promise<void> {
  return api.put(`${getDeviceBasePath()}/dashboard/${deviceId}`).then((res) => res.data.data);
}

export async function createAlert(alertDto: AlertCreateDto) {
  return api.post(`${getDeviceBasePath()}/parameter/alert`, alertDto).then((res) => res.data.data);
}

export async function updateAlert(alertDto: AlertUpdateDto, alertId: string) {
  return api
    .put(`${getDeviceBasePath()}/parameter/alert/${alertId}`, alertDto)
    .then((res) => res.data.data);
}

export async function deleteAlert(alertId: string) {
  return api
    .delete(`${getDeviceBasePath()}/parameter/alert/${alertId}`)
    .then((res) => res.data.data);
}

export async function getLocationAlerts(context: QueryFunctionContext): Promise<Alert[]> {
  const locationId = context.queryKey[1];
  return api
    .get(`${getDeviceBasePath()}/location-alert/${locationId}`)
    .then((res) => res.data.data);
}

export async function getLocationHistory(context: QueryFunctionContext): Promise<AlertHistory[]> {
  const locationId = context.queryKey[1];
  const fromDate = context.queryKey[2];
  const toDate = context.queryKey[3];
  if (fromDate || toDate) {
    return api
      .get(`${getDeviceBasePath()}/location-alert-history/${locationId}`, {
        params: { fromDate, toDate },
      })
      .then((res) => res.data.data);
  }
  return api
    .get(`${getDeviceBasePath()}/location-alert-history/${locationId}`)
    .then((res) => res.data.data);
}

export async function getPreSignedUrlForUpload(
  uploadDto: UploadRequestDto
): Promise<UploadResponseDto> {
  return api.post(`${getAwsBasePath()}/get-upload-url`, uploadDto).then((res) => res.data.data);
}

export async function getPreSignedUrlForDownload(
  download: DownloadRequestDto
): Promise<DownloadResponseDto> {
  return api.post(`${getAwsBasePath()}/get-download-url`, download).then((res) => res.data.data);
}

// ==========================================
// COMPLIANCE OPERATIONS
// ==========================================

import type {
  ComplianceProfile,
  ComplianceProfileCreateDto,
  ComplianceProfileUpdateDto,
  ComplianceCheckResult,
} from "../types/compliance";

const getComplianceBasePath = () => "/compliance";

export async function createComplianceProfile(
  data: ComplianceProfileCreateDto
): Promise<ComplianceProfile> {
  return api.post(getComplianceBasePath(), data).then((res) => res.data.data);
}

export async function getAllComplianceProfiles(): Promise<ComplianceProfile[]> {
  return api.get(getComplianceBasePath()).then((res) => res.data.data);
}

export async function getComplianceProfile(
  context: QueryFunctionContext
): Promise<ComplianceProfile> {
  const id = context.queryKey[2];
  return api.get(`${getComplianceBasePath()}/${id}`).then((res) => res.data.data);
}

export async function updateComplianceProfile(
  id: string,
  data: ComplianceProfileUpdateDto
): Promise<ComplianceProfile> {
  return api.put(`${getComplianceBasePath()}/${id}`, data).then((res) => res.data.data);
}

export async function deleteComplianceProfile(id: string): Promise<void> {
  return api.delete(`${getComplianceBasePath()}/${id}`).then((res) => res.data.data);
}

export async function checkCompliance(
  context: QueryFunctionContext
): Promise<ComplianceCheckResult> {
  const profileId = context.queryKey[2] as string;
  const days = context.queryKey[3] as number;
  return api
    .get(`${getComplianceBasePath()}/${profileId}/check`, { params: { days } })
    .then((res) => res.data.data);
}

export async function downloadComplianceReport(
  profileId: string,
  days: number
): Promise<void> {
  const response = await api.get(`${getComplianceBasePath()}/${profileId}/report`, {
    params: { days },
    responseType: "blob",
  });
  const blob = new Blob([response.data], { type: "application/pdf" });
  const contentDisposition = response.headers["content-disposition"];
  let filename = "Compliance_Report.pdf";
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) filename = match[1];
  }
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
